import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import { useCartCtx, useAuthCtx } from '../App';
import { useCurrency } from '../hooks/useCurrency';
import { useRewardConfig } from '../hooks/useRewardConfig';
import api from '../utils/api';
import { toast } from 'react-toastify';
import RewardsLadder from '../components/storefront/RewardsLadder';

/* ════════════════════════════════════════════════════════════════════════
   THE APOTHECARY'S VANITY
   A maximalist, botanical-damask shopping cart for AB8 Beauty & Skin.
   Gilded ornaments · rewards ladder · promo rituals · gift notes ·
   "Complete Your Ritual" upsell · floating petals over a living wallpaper.
   ════════════════════════════════════════════════════════════════════════ */

// The rewards ladder (free-shipping threshold, gift tiers, flat rate, gift value)
// is admin-tunable — see /admin/rewards and useRewardConfig. Read inside the
// component from that hook; DEFAULT_REWARDS mirrors the historic constants.

// Static petal field (no per-render randomness).
const PETALS = [
  { left: '6%',  size: 20, dur: 17, delay: 0,   hue: '#f9a8d0' },
  { left: '17%', size: 13, dur: 22, delay: 6,   hue: '#f472b6' },
  { left: '28%', size: 24, dur: 15, delay: 3,   hue: '#fbcfe2' },
  { left: '39%', size: 11, dur: 25, delay: 9,   hue: '#f9a8d0' },
  { left: '51%', size: 18, dur: 19, delay: 1.5, hue: '#ec4899' },
  { left: '63%', size: 14, dur: 23, delay: 7,   hue: '#fbcfe2' },
  { left: '74%', size: 22, dur: 16, delay: 4.5, hue: '#f472b6' },
  { left: '83%', size: 12, dur: 21, delay: 11,  hue: '#f9a8d0' },
  { left: '92%', size: 19, dur: 18, delay: 2.5, hue: '#fbcfe2' },
];

/* ── A single rose petal shape ──────────────────────────────────────────── */
function Petal({ size, hue }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M12 2 C 18 6, 20 14, 12 22 C 4 14, 6 6, 12 2 Z" fill={hue} fillOpacity="0.85" />
      <path d="M12 4 C 12 10, 12 16, 12 21" stroke="#be185d" strokeOpacity="0.3" strokeWidth="0.8" />
    </svg>
  );
}

/* ── Living wallpaper: damask + washes + drifting petals ────────────────── */
function CartWallpaper() {
  return (
    <div className="cart-wallpaper" aria-hidden="true">
      <div className="cart-wash" />
      <svg className="cart-damask" width="100%" height="100%">
        <defs>
          <g id="ab8-sprig" strokeLinecap="round">
            {/* stem + leaves */}
            <path d="M0,62 C -5,42 5,30 0,8" fill="none" stroke="#be185d" strokeOpacity="0.32" strokeWidth="1.4" />
            <path d="M0,48 C -24,42 -32,22 -32,10 C -16,16 -4,28 0,42 Z" fill="#f472b6" fillOpacity="0.10" stroke="#be185d" strokeOpacity="0.26" strokeWidth="1.2" />
            <path d="M0,48 C 24,42 32,22 32,10 C 16,16 4,28 0,42 Z" fill="#f472b6" fillOpacity="0.10" stroke="#be185d" strokeOpacity="0.26" strokeWidth="1.2" />
            {/* blossom — six petals in a ring */}
            <g fill="#f472b6" fillOpacity="0.16" stroke="#be185d" strokeOpacity="0.30" strokeWidth="1.1">
              <ellipse cx="0" cy="-15" rx="7.5" ry="13" />
              <ellipse cx="0" cy="-15" rx="7.5" ry="13" transform="rotate(60)" />
              <ellipse cx="0" cy="-15" rx="7.5" ry="13" transform="rotate(120)" />
              <ellipse cx="0" cy="-15" rx="7.5" ry="13" transform="rotate(180)" />
              <ellipse cx="0" cy="-15" rx="7.5" ry="13" transform="rotate(240)" />
              <ellipse cx="0" cy="-15" rx="7.5" ry="13" transform="rotate(300)" />
            </g>
            <circle cx="0" cy="0" r="5" fill="#d4af37" fillOpacity="0.5" />
            {/* berries */}
            <circle cx="-34" cy="8" r="2.4" fill="#d4af37" fillOpacity="0.5" />
            <circle cx="34" cy="8" r="2.4" fill="#d4af37" fillOpacity="0.5" />
          </g>
          <pattern id="ab8-damask" x="0" y="0" width="240" height="320" patternUnits="userSpaceOnUse">
            <use href="#ab8-sprig" transform="translate(60,76)" />
            <use href="#ab8-sprig" transform="translate(180,236) scale(0.9)" />
            <circle cx="180" cy="70" r="2" fill="#be185d" fillOpacity="0.18" />
            <circle cx="60"  cy="232" r="2" fill="#be185d" fillOpacity="0.18" />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#ab8-damask)" />
      </svg>
      <div className="cart-grain" />
      <div className="cart-vignette" />
      <div className="cart-wallpaper">
        {PETALS.map((p, i) => (
          <span
            key={i}
            className="cart-petal"
            style={{ left: p.left, animationDuration: `${p.dur}s`, animationDelay: `${p.delay}s` }}
          >
            <Petal size={p.size} hue={p.hue} />
          </span>
        ))}
      </div>
    </div>
  );
}

/* ── Gilded corner flourish for ornate frames ───────────────────────────── */
function Corners() {
  const flourish = (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round">
      <path d="M2 2 L2 9 M2 2 L9 2" />
      <path d="M2 2 C 10 4, 12 8, 11 12" strokeOpacity="0.7" />
      <circle cx="6" cy="6" r="1.4" fill="currentColor" stroke="none" />
    </svg>
  );
  return (
    <>
      <span className="cart-corner tl">{flourish}</span>
      <span className="cart-corner tr">{flourish}</span>
      <span className="cart-corner bl">{flourish}</span>
      <span className="cart-corner br">{flourish}</span>
    </>
  );
}

/* ── Bottle fallback glyph ──────────────────────────────────────────────── */
function BottleGlyph({ className }) {
  return (
    <svg viewBox="0 0 64 64" className={className} fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <rect x="22" y="6" width="20" height="10" rx="2" />
      <rect x="20" y="16" width="24" height="6" />
      <path d="M22 22h20l-2 34a4 4 0 0 1-4 4h-8a4 4 0 0 1-4-4z" />
      <path d="M26 30h12M26 40h12" strokeOpacity="0.4" />
    </svg>
  );
}

export default function CartPage() {
  const { items, removeItem, updateQuantity, addItem, clearCart, total, coupon, setCoupon, clearCoupon, cartEmail, setCartEmail } = useCartCtx();
  const { user } = useAuthCtx();
  const { fmt } = useCurrency();
  const rewards = useRewardConfig();

  // Admin-tunable reward tiers (with the historic defaults baked into the hook).
  const FREE_SHIP_AT = rewards.freeShippingThresholdCents;
  const FREE_BALM_AT = rewards.freeGiftThresholdCents;
  const FREE_GIFT_AT = rewards.mysteryGiftThresholdCents;
  const SHIP_FLAT    = rewards.flatShippingRateCents;
  const BALM_VALUE   = rewards.freeGiftValueCents;
  const balmName     = rewards.freeGiftName || 'free gift';
  const giftName     = rewards.mysteryGiftName || 'mystery gift';
  const giftsOn      = rewards.freeGiftEnabled;

  const [promoInput, setPromoInput]   = useState('');
  const [promoError, setPromoError]   = useState('');
  const [applyingPromo, setApplyingPromo] = useState(false);
  const [giftOpen, setGiftOpen]       = useState(false);
  const [giftNote, setGiftNote]     = useState('');
  const [recommended, setRecommended] = useState([]);
  const [celebrated, setCelebrated] = useState(false);

  // ── Recommended "ritual" add-ons (anything not already in the cart) ──────
  useEffect(() => {
    api.get('/products', { params: { limit: 8 } })
      .then(res => setRecommended(res.data.data || []))
      .catch(() => {});
  }, []);

  // Re-validate a persisted coupon whenever the subtotal changes, so the preview
  // discount stays accurate (and a coupon that no longer qualifies is dropped).
  // Checkout re-validates again server-side, so the charge is always correct.
  useEffect(() => {
    if (!coupon?.code || total <= 0) return;
    let cancelled = false;
    api.post('/orders/validate-coupon', {
      code: coupon.code,
      subtotalInCents: total,
      shippingInCents: total >= FREE_SHIP_AT ? 0 : SHIP_FLAT,
      scope: 'one_time',
    })
      .then(({ data }) => {
        if (cancelled) return;
        if (data.discountInCents !== coupon.discountInCents || data.freeShipping !== coupon.freeShipping) {
          setCoupon({ code: data.code, description: data.description, discountInCents: data.discountInCents, freeShipping: data.freeShipping });
        }
      })
      .catch(err => {
        // 422 = no longer valid at this subtotal; 401 = session lost. Drop it quietly.
        if (!cancelled && [401, 422].includes(err.response?.status)) {
          clearCoupon();
          toast.info(`Charm ${coupon.code} removed — ${err.response?.data?.error || 'no longer eligible'}`);
        }
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [total, coupon?.code]);

  const inCart = useMemo(() => new Set(items.map(i => i.variantId)), [items]);
  const ritual = recommended
    .filter(p => p.variants?.[0] && !inCart.has(p.variants[0].id))
    .slice(0, 3);

  // ── Money math ───────────────────────────────────────────────────────────
  // Discounts are validated + applied server-side at checkout; the figures here
  // mirror the backend coupon rules so the cart preview matches the final charge.
  const freeShipByThreshold = total >= FREE_SHIP_AT;
  const freeShipUnlocked = freeShipByThreshold || coupon?.freeShipping;
  const balmUnlocked     = giftsOn && total >= FREE_BALM_AT;
  const giftUnlocked     = giftsOn && total >= FREE_GIFT_AT;
  const shipping = freeShipUnlocked ? 0 : SHIP_FLAT;

  // free_shipping coupons discount the shipping line, not the subtotal.
  const discount = coupon && !coupon.freeShipping ? Math.min(coupon.discountInCents || 0, total) : 0;
  const grandTotal = Math.max(0, total - discount) + shipping;

  // Celebrate the moment everything is unlocked.
  useEffect(() => {
    if (giftUnlocked && !celebrated) {
      setCelebrated(true);
      toast.success(`✦ You unlocked free shipping, a ${balmName} + a ${giftName}!`);
    }
    if (!giftUnlocked && celebrated) setCelebrated(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [giftUnlocked, celebrated]);

  const applyPromo = async (e) => {
    e.preventDefault();
    const code = promoInput.trim().toUpperCase();
    if (!code || applyingPromo) return;
    setApplyingPromo(true);
    setPromoError('');
    try {
      // Same endpoint checkout uses — the backend is the single source of truth.
      const { data } = await api.post('/orders/validate-coupon', {
        code,
        subtotalInCents: total,
        shippingInCents: freeShipByThreshold ? 0 : SHIP_FLAT,
        scope: 'one_time',
      });
      setCoupon({
        code: data.code,
        description: data.description,
        discountInCents: data.discountInCents,
        freeShipping: data.freeShipping,
      });
      setPromoInput('');
      toast.success(`Applied ${data.code}${data.description ? ` — ${data.description}` : ''}`);
    } catch (err) {
      const status = err.response?.status;
      if (status === 401) {
        setPromoError('Sign in to apply an apothecary charm.');
      } else {
        setPromoError(err.response?.data?.error || 'That charm isn’t in our grimoire.');
      }
    } finally {
      setApplyingPromo(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col relative">
      <CartWallpaper />

      <div className="relative z-10 flex flex-col min-h-screen">
        <Navbar />

        <main className="flex-1 max-w-6xl mx-auto px-4 sm:px-6 py-12 w-full">
          {/* ── Ornate masthead ─────────────────────────────────────────── */}
          <header className="text-center mb-10">
            <p className="font-display italic text-tea-600 dark:text-tea-400 text-lg tracking-wide">
              AB8 · Beauty &amp; Skin
            </p>
            <h1 className="font-display text-5xl sm:text-6xl font-bold cart-gild leading-tight">
              AB8 Shopping Cart
            </h1>
            <div className="cart-rule max-w-md mx-auto mt-5" />
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-4 tracking-[0.18em] uppercase">
              {items.length === 0
                ? 'Your vanity awaits'
                : `${items.reduce((s, i) => s + i.quantity, 0)} treasure${
                    items.reduce((s, i) => s + i.quantity, 0) === 1 ? '' : 's'
                  } gathered`}
            </p>
          </header>

          {items.length === 0 ? (
            /* ── Empty state ───────────────────────────────────────────── */
            <div className="cart-frame max-w-xl mx-auto text-center px-8 py-16">
              <Corners />
              <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-gradient-to-br from-tea-50 to-tea-100 dark:from-tea-900/40 dark:to-tea-900/10 flex items-center justify-center text-tea-500">
                <BottleGlyph className="w-12 h-12" />
              </div>
              <h2 className="font-display text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2">
                A cart as empty as a fresh apothecary jar
              </h2>
              <p className="text-gray-500 dark:text-gray-400 mb-8">
                Fill it with peptide-rich serums, bee-venom balms &amp; boho-luxe botanicals.
              </p>
              <Link to="/shop" className="cart-checkout inline-flex px-8 py-3.5 rounded-full font-semibold tracking-wide">
                Wander the Collection ✦
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-[1.6fr_1fr] gap-8 items-start">
              {/* ════════ LEFT: ladder + items + ritual ════════ */}
              <div className="space-y-6">
                {/* ── Rewards ladder (shared with checkout; admin-tunable) ── */}
                <RewardsLadder total={total} freeShippingCoupon={!!coupon?.freeShipping} />

                {/* ── Item scrolls ────────────────────────────────────── */}
                <div className="space-y-4">
                  {items.map(item => (
                    <article key={item.variantId} className="cart-frame p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-4">
                      <Corners />
                      <div className="flex items-center gap-4 sm:contents">
                        <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-xl bg-gradient-to-br from-tea-50 to-tea-100 dark:from-tea-900/40 dark:to-tea-900/10 flex items-center justify-center flex-shrink-0 overflow-hidden ring-1 ring-amber-300/50">
                          {item.imageUrl ? (
                            <img src={item.imageUrl} alt={item.productName} className="w-full h-full object-cover" />
                          ) : (
                            <BottleGlyph className="w-10 h-10 text-tea-500" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-display text-xl font-bold text-gray-900 dark:text-gray-100 truncate">
                            {item.productName}
                          </h3>
                          <p className="text-xs uppercase tracking-[0.15em] text-gray-500 dark:text-gray-400">
                            {item.variantName}
                          </p>
                          <p className="text-tea-700 dark:text-tea-300 font-semibold mt-1">{fmt(item.priceInCents)} <span className="text-gray-400 text-xs font-normal">each</span></p>
                        </div>
                      </div>

                      <div className="flex items-center justify-between sm:justify-end gap-4 sm:gap-5 sm:ml-auto">
                        <div className="cart-qty flex items-center">
                          <button onClick={() => updateQuantity(item.variantId, item.quantity - 1)}
                            className="cart-qty-btn px-3.5 py-2 text-gray-500 dark:text-gray-300 text-lg" aria-label="Decrease quantity">−</button>
                          <span className="px-2 min-w-[2ch] text-center font-bold text-gray-900 dark:text-gray-100">{item.quantity}</span>
                          <button onClick={() => updateQuantity(item.variantId, item.quantity + 1)}
                            className="cart-qty-btn px-3.5 py-2 text-gray-500 dark:text-gray-300 text-lg" aria-label="Increase quantity">+</button>
                        </div>
                        <p className="font-display text-xl font-bold w-20 text-right cart-gild">
                          {fmt(item.priceInCents * item.quantity)}
                        </p>
                        <button onClick={() => removeItem(item.variantId)}
                          className="cart-remove text-gray-400 p-1" aria-label="Remove item">
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    </article>
                  ))}

                  {/* ── Free Bubblé balm stick unlock ─────────────────── */}
                  {balmUnlocked && (
                    <article className="cart-frame cart-gift-glow p-5 flex items-center gap-4 overflow-hidden relative">
                      <div className="absolute -top-3 -right-3 bg-gradient-to-br from-pink-400 to-pink-600 text-white text-[10px] font-bold tracking-wider px-3 py-1 rounded-bl-lg rounded-tr-xl shadow z-10">
                        FREE
                      </div>
                      <div className="w-20 h-20 rounded-xl bg-gradient-to-br from-pink-100 to-pink-200 dark:from-pink-900/40 dark:to-pink-900/10 flex items-center justify-center flex-shrink-0 ring-1 ring-pink-300/60">
                        <svg viewBox="0 0 64 64" className="w-14 h-14" aria-hidden="true">
                          <defs>
                            <linearGradient id="balmCase" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="0%" stopColor="#f472b6" /><stop offset="100%" stopColor="#be185d" />
                            </linearGradient>
                            <linearGradient id="balmStick" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="0%" stopColor="#fbcfe2" /><stop offset="100%" stopColor="#f9a8d0" />
                            </linearGradient>
                          </defs>
                          {/* balm stick, twisted up out of its case */}
                          <rect x="24" y="34" width="16" height="26" rx="3" fill="url(#balmCase)" />
                          <rect x="24" y="32" width="16" height="5" rx="2" fill="#9d174d" />
                          <path d="M26 34h12v-9a6 6 0 0 0-12 0z" fill="url(#balmStick)" />
                          <ellipse cx="32" cy="25" rx="6" ry="2.4" fill="#fce7f0" />
                          <circle cx="30" cy="22" r="1.3" fill="#fff" fillOpacity="0.8" />
                        </svg>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-display text-xl font-bold text-pink-900 dark:text-pink-200 flex items-center gap-2">
                          Bubblé — Pink Collagen Balm Stick
                          <span className="cart-chip">FREE</span>
                        </p>
                        <p className="text-sm text-pink-700 dark:text-pink-300/80">A full-size dewy-flush balm stick, on us with orders over {fmt(FREE_BALM_AT)}.</p>
                      </div>
                      <p className="font-display text-lg font-bold text-tea-700 dark:text-tea-300 flex items-baseline gap-2">
                        <span className="text-gray-400 line-through text-sm font-normal">{fmt(BALM_VALUE)}</span>
                        {fmt(0)}
                      </p>
                    </article>
                  )}

                  {/* ── Mystery gift unlock ───────────────────────────── */}
                  {giftUnlocked && (
                    <article className="cart-frame cart-gift-glow p-5 flex items-center gap-4 overflow-hidden relative">
                      <div className="absolute -top-3 -right-3 bg-gradient-to-br from-amber-400 to-amber-600 text-white text-[10px] font-bold tracking-wider px-3 py-1 rounded-bl-lg rounded-tr-xl shadow z-10">
                        GIFT
                      </div>
                      <div className="w-20 h-20 rounded-xl bg-gradient-to-br from-amber-100 to-amber-200 flex items-center justify-center flex-shrink-0">
                        <svg viewBox="0 0 64 64" className="w-14 h-14" aria-hidden="true">
                          <defs>
                            <linearGradient id="giftBox" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="0%" stopColor="#b45309" /><stop offset="100%" stopColor="#78350f" />
                            </linearGradient>
                            <linearGradient id="giftRibbon" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="0%" stopColor="#fbbf24" /><stop offset="100%" stopColor="#d97706" />
                            </linearGradient>
                          </defs>
                          <rect x="8" y="24" width="48" height="32" rx="2" fill="url(#giftBox)" />
                          <rect x="8" y="22" width="48" height="8" rx="2" fill="#92400e" />
                          <rect x="28" y="22" width="8" height="34" fill="url(#giftRibbon)" />
                          <path d="M32 22 C 22 14, 14 16, 18 20 C 22 24, 28 22, 32 22 Z" fill="url(#giftRibbon)" />
                          <path d="M32 22 C 42 14, 50 16, 46 20 C 42 24, 36 22, 32 22 Z" fill="url(#giftRibbon)" />
                          <circle cx="32" cy="22" r="3" fill="#fbbf24" />
                        </svg>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-display text-xl font-bold text-amber-900 dark:text-amber-200 flex items-center gap-2">
                          Mystery Wellness Gift
                          <span className="cart-chip">FREE</span>
                        </p>
                        <p className="text-sm text-amber-700 dark:text-amber-300/80">A hand-wrapped thank-you, tucked in with orders over $300.</p>
                      </div>
                      <p className="font-display text-lg font-bold text-tea-700 dark:text-tea-300">$0.00</p>
                    </article>
                  )}
                </div>

                {/* ── Complete your ritual ────────────────────────────── */}
                {ritual.length > 0 && (
                  <section className="cart-frame p-6">
                    <Corners />
                    <div className="flex items-center gap-3 mb-1">
                      <h2 className="font-display text-2xl font-bold text-gray-900 dark:text-gray-100">Complete Your Ritual</h2>
                    </div>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-5">Curated companions for what&rsquo;s in your cart.</p>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      {ritual.map(p => {
                        const v = p.variants[0];
                        return (
                          <div key={p.id} className="group rounded-xl border border-amber-300/40 bg-white/60 dark:bg-tea-900/10 p-3 flex flex-col text-center transition-transform hover:-translate-y-1">
                            <Link to={`/shop/${p.slug}`} className="block">
                              <div className="w-full h-28 rounded-lg bg-gradient-to-br from-tea-50 to-tea-100 dark:from-tea-900/40 dark:to-tea-900/10 flex items-center justify-center overflow-hidden mb-2">
                                {p.imageUrl ? (
                                  <img src={p.imageUrl} alt={p.name}
                                    className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                                    onError={e => { e.currentTarget.style.display = 'none'; e.currentTarget.nextSibling.style.display = 'flex'; }} />
                                ) : null}
                                <div style={{ display: p.imageUrl ? 'none' : 'flex' }} className="w-full h-full items-center justify-center">
                                  <BottleGlyph className="w-10 h-10 text-tea-500" />
                                </div>
                              </div>
                              <p className="font-display font-bold text-gray-900 dark:text-gray-100 leading-tight">{p.name}</p>
                            </Link>
                            <p className="text-tea-700 dark:text-tea-300 font-semibold text-sm mt-1 mb-2">{fmt(v.priceInCents)}</p>
                            <button
                              onClick={() => { addItem(v, p); toast.success(`${p.name} joined your ritual`); }}
                              className="mt-auto text-xs font-semibold tracking-wide border border-tea-600 text-tea-700 dark:text-tea-300 dark:border-tea-500 rounded-full py-2 hover:bg-tea-600 hover:text-white transition-colors">
                              + Add to Cart
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </section>
                )}

                <button onClick={clearCart}
                  className="text-xs uppercase tracking-[0.2em] text-gray-400 hover:text-tea-600 transition-colors">
                  Empty the cart
                </button>
              </div>

              {/* ════════ RIGHT: summary ════════ */}
              <aside className="lg:sticky lg:top-24 space-y-5">
                <div className="cart-frame p-6">
                  <Corners />
                  <h2 className="font-display text-3xl font-bold cart-gild text-center mb-1">Your Receipt</h2>
                  <div className="cart-rule mb-5" />

                  {/* Promo ritual */}
                  <form onSubmit={applyPromo} className="mb-5">
                    <label className="text-xs uppercase tracking-[0.15em] text-gray-500 dark:text-gray-400">Apothecary charm</label>
                    <div className="flex gap-2 mt-1.5">
                      <input
                        value={promoInput}
                        onChange={e => { setPromoInput(e.target.value); setPromoError(''); }}
                        placeholder="e.g. BLOOM10"
                        className="input-field flex-1 uppercase tracking-wide" />
                      <button type="submit" disabled={applyingPromo || !promoInput.trim()}
                        className="btn-secondary px-5 py-2 whitespace-nowrap disabled:opacity-50">
                        {applyingPromo ? '…' : 'Apply'}
                      </button>
                    </div>
                    {promoError && <p className="text-xs text-red-500 mt-1.5">{promoError}</p>}
                    {coupon && (
                      <div className="flex items-center justify-between mt-2.5">
                        <span className="cart-chip">✦ {coupon.code}{coupon.description ? ` — ${coupon.description}` : ''}</span>
                        <button type="button" onClick={clearCoupon}
                          className="text-xs text-gray-400 hover:text-red-500">remove</button>
                      </div>
                    )}
                  </form>

                  {/* Gift note ritual */}
                  <div className="mb-5">
                    <button type="button" onClick={() => setGiftOpen(o => !o)}
                      className="w-full flex items-center justify-between text-sm font-medium text-gray-700 dark:text-gray-200">
                      <span className="flex items-center gap-2">
                        <svg className="w-4 h-4 text-tea-600" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M21 11.5V21H3v-9.5M1 7h22v4.5H1zM12 7v14M12 7S9 2 6.5 4 8 7 12 7zM12 7s3-5 5.5-3S16 7 12 7z" />
                        </svg>
                        Add a handwritten gift note
                      </span>
                      <span className={`transition-transform ${giftOpen ? 'rotate-45' : ''} text-tea-600 text-lg`}>+</span>
                    </button>
                    {giftOpen && (
                      <textarea
                        value={giftNote}
                        onChange={e => setGiftNote(e.target.value)}
                        maxLength={240}
                        rows={3}
                        placeholder="“For someone radiant…” — penned in gold ink at our counter."
                        className="input-field mt-2.5 resize-none font-display italic text-base" />
                    )}
                  </div>

                  <div className="cart-rule mb-5" />

                  {/* Tallies */}
                  <dl className="space-y-2.5 text-sm">
                    <div className="flex justify-between text-gray-700 dark:text-gray-300">
                      <dt>Subtotal</dt><dd>{fmt(total)}</dd>
                    </div>
                    {discount > 0 && (
                      <div className="flex justify-between text-tea-600 dark:text-tea-400 font-medium">
                        <dt>Charm discount</dt><dd>−{fmt(discount)}</dd>
                      </div>
                    )}
                    <div className="flex justify-between text-gray-700 dark:text-gray-300">
                      <dt>Shipping</dt>
                      <dd>{shipping === 0
                        ? <span className="text-tea-600 dark:text-tea-400 font-semibold">FREE</span>
                        : fmt(shipping)}</dd>
                    </div>
                    {giftUnlocked && (
                      <div className="flex justify-between text-amber-600 dark:text-amber-400 font-medium">
                        <dt>Mystery gift</dt><dd>Included ✦</dd>
                      </div>
                    )}
                  </dl>

                  <div className="cart-rule my-5" />

                  <div className="flex items-end justify-between mb-6">
                    <span className="font-display text-2xl font-bold text-gray-900 dark:text-gray-100">Total</span>
                    <span className="font-display text-4xl font-bold cart-gild">{fmt(grandTotal)}</span>
                  </div>

                  {/* Guest cart-saver — lets us email a recovery link if they
                      wander off before signing in to check out. */}
                  {!user && (
                    <div className="mb-4">
                      <label htmlFor="cart-email" className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1.5">
                        Email me my cart so you don't lose it
                      </label>
                      <input
                        id="cart-email"
                        type="email"
                        value={cartEmail}
                        onChange={e => setCartEmail(e.target.value)}
                        placeholder="you@email.com"
                        autoComplete="email"
                        className="input-field"
                      />
                    </div>
                  )}

                  <Link to="/checkout" className="cart-checkout w-full flex items-center justify-center px-6 py-4 rounded-full font-semibold text-lg tracking-wide">
                    Proceed to Checkout ✦
                  </Link>

                  {/* Trust badges */}
                  <div className="grid grid-cols-3 gap-2 mt-6 text-center">
                    {[
                      { t: 'Vegan', d: '100% plant-kind' },
                      { t: 'Cruelty-Free', d: 'Never tested' },
                      { t: 'Secure', d: 'Stripe checkout' },
                    ].map(b => (
                      <div key={b.t} className="rounded-lg border border-amber-300/40 bg-white/50 dark:bg-tea-900/10 py-2 px-1">
                        <svg className="w-5 h-5 mx-auto text-tea-600 dark:text-tea-400 mb-1" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <p className="text-[11px] font-bold text-gray-800 dark:text-gray-200">{b.t}</p>
                        <p className="text-[9px] text-gray-500 dark:text-gray-400 leading-tight">{b.d}</p>
                      </div>
                    ))}
                  </div>
                </div>

                <p className="text-center text-xs text-gray-500 dark:text-gray-400 tracking-wide">
                  Free shipping over {fmt(FREE_SHIP_AT)}{giftsOn ? ` · Gift over ${fmt(FREE_GIFT_AT)}` : ''} · 30-day radiance guarantee
                </p>
              </aside>
            </div>
          )}
        </main>

        <Footer />
      </div>
    </div>
  );
}
